import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../services/authAPI";
import { validateOTP, formatAPIError } from "../utils/validation";
import Header from "../components/layout/Header";
import "./styles/MFAVerification.css";

const MFAVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const userData = location.state?.userData;
  const mfaType = location.state?.mfaType; // 'email' or 'phone'
  const identifier = location.state?.identifier;
  const demoHint = location.state?.demoHint; // optional

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!userData || !identifier) {
      navigate("/login");
    }
  }, [userData, identifier, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const maskedIdentifier = identifier
    ? mfaType === "email"
      ? identifier.replace(/(.{2})(.*)(@.*)/, "$1***$3")
      : identifier.replace(/(\+\d{2})(\d{3})(\d+)/, "$1***$3")
    : "";

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtp(value);
    if (error) setError("");
  };

  const setDigit = (index, digit) => {
    if (!/^\d?$/.test(digit)) return;
    const chars = otp.split("");
    while (chars.length < 6) chars.push("");
    chars[index] = digit;
    const next = chars.join("").slice(0, 6).replace(/\s/g, "");
    setOtp(next);

    const nextEl = document.getElementById(`mfa-otp-${index + 1}`);
    if (digit && nextEl) nextEl.focus();
  };

  const handleBoxKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const chars = otp.split("");
        chars[index] = "";
        setOtp(chars.join(""));
        return;
      }
      const prevEl = document.getElementById(`mfa-otp-${index - 1}`);
      if (prevEl) prevEl.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpError = validateOTP(otp);
    if (otpError) {
      setError(otpError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const mfaData = { otp };
      if (mfaType === "email") mfaData.email = identifier;
      else mfaData.phone = identifier;

      const response = await authAPI.verifyMFA(mfaData);

      setSuccess("Authentication successful!");

      if (response.data.token) {
        let backendUser = response.data.user || response.data.userData || response.data.data?.user;

        const finalUserData = {
          ...userData,
          ...(backendUser || {}),
          role: backendUser?.role || userData?.role,
        };

        if (!finalUserData.role) {
          throw new Error("User role not found after MFA verification");
        }

        setTimeout(() => {
          login(finalUserData, response.data.token);
          navigate("/dashboard", { state: { message: "Successfully logged in with MFA!" } });
        }, 1500);
      } else {
        throw new Error("No authentication token received from backend");
      }
    } catch (err) {
      const errorMessage = formatAPIError(err);

      if (errorMessage.includes("Invalid or expired OTP")) {
        setError("Invalid or expired OTP. Please request a new one.");
      } else if (errorMessage.includes("MFA is not enabled")) {
        setError("Multi-factor authentication is not enabled for this account.");
        setTimeout(() => navigate("/login"), 3000);
      } else if (errorMessage.includes("role not found")) {
        setError("Authentication error: User role not found. Please contact support.");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError("");

    try {
      const loginData = {
        [mfaType === "email" ? "email" : "phone"]: identifier,
        password: location.state?.password,
      };

      await authAPI.login(loginData);

      setSuccess(`New OTP sent to your ${mfaType}!`);
      setResendCooldown(60);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentMethod = () => {
    // Keep it simple: go back to login (or route to choose method if you have one)
    navigate("/login");
  };

  // success screen (simple card)
  if (success && success.toLowerCase().includes("successful")) {
    return (
      <>
        <Header />
        <div className="mfa-page">
          <div className="mfa-card">
            <div className="mfa-top">
              <div className="mfa-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4Z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h1 className="mfa-title">Verified</h1>
              <p className="mfa-subtitle">Redirecting to dashboard...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="mfa-page">
        <div className="mfa-card">
          <div className="mfa-top">
            <div className="mfa-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4Z"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="mfa-title">Two-Factor Authentication</h1>
            <p className="mfa-subtitle">Enter the 6-digit code from your authenticator app</p>
          </div>

          <div className="mfa-body">
            {error && <div className="mfa-alert mfa-alert--error">{error}</div>}
            {success && <div className="mfa-alert mfa-alert--success">{success}</div>}

            {/* Hidden input keeps your existing otp string logic */}
            <input
              className="mfa-hidden"
              inputMode="numeric"
              value={otp}
              onChange={handleOTPChange}
              aria-hidden="true"
              tabIndex={-1}
            />

            <form onSubmit={handleSubmit} className="mfa-form">
              <div className="mfa-otpRow" aria-label="OTP input">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    id={`mfa-otp-${i}`}
                    className="mfa-otpBox"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[i] || ""}
                    onChange={(e) => setDigit(i, e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => handleBoxKeyDown(i, e)}
                    disabled={loading}
                  />
                ))}
              </div>

              <div className="mfa-hint">
                {demoHint ||
                  "For demo purposes: Enter 123456 to simulate successful verification"}
              </div>

              {/* optional small masked identifier line (not in your screenshot, but helpful) */}
              {maskedIdentifier && (
                <div className="mfa-meta">
                  Verifying for {mfaType === "email" ? "email" : "phone"}: {maskedIdentifier}
                </div>
              )}

              <button className="mfa-btn mfa-btn--primary" type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Verify Code"}
              </button>

              <div className="mfa-links">
                <div className="mfa-muted">Didn't receive a code?</div>

                <button
                  type="button"
                  className="mfa-linkBtn"
                  onClick={handleResendOTP}
                  disabled={loading || resendCooldown > 0}
                >
                  {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : "Resend Code"}
                </button>

                <button
                  type="button"
                  className="mfa-linkBtn"
                  onClick={handleUseDifferentMethod}
                  disabled={loading}
                >
                  Use Different Method
                </button>

                <Link to="/login" className="mfa-linkBack">
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default MFAVerification;
